import { useRef, useState } from "react";
import {
  Video,
  FileText,
  HelpCircle,
  Link as LinkIcon,
  Paperclip,
  Radio,
  Upload,
  CheckCircle2,
  Loader2,
  Eye,
  Image as ImageIcon,
  Trash2,
  Download,
} from "lucide-react";
import { FormField, inputClass, buttonClass, Modal } from "./shared";
import RichTextEditor from "./RichTextEditor";
import QuizEditor from "./QuizEditor";
import useLearningPlatform from "../../../api/learningPlatform";

const LESSON_TYPES = [
  { value: "video", label: "Video", icon: Video, hint: "Eigen video upload" },
  { value: "text", label: "Tekst / artikel", icon: FileText, hint: "Lange leestekst" },
  { value: "embed", label: "Embed", icon: LinkIcon, hint: "YouTube / Vimeo" },
  { value: "file", label: "Downloadbestand", icon: Paperclip, hint: "PDF, ZIP, ..." },
  { value: "quiz", label: "Quiz", icon: HelpCircle, hint: "Multiple choice" },
  { value: "live", label: "Live sessie", icon: Radio, hint: "Zoom / Teams link" },
];

const formatDuration = (seconds) => {
  const m = Math.floor((seconds || 0) / 60);
  const s = Math.floor((seconds || 0) % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
};

const LessonEditorModal = ({
  isDark,
  lesson,
  modules,
  onChange,
  onClose,
  onSave,
}) => {
  const { uploadMedia } = useLearningPlatform();
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [saving, setSaving] = useState(false);
  const [fileError, setFileError] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [attachmentBusy, setAttachmentBusy] = useState(false);
  const [downloadFileBusy, setDownloadFileBusy] = useState(false);
  const inputRef = useRef(null);
  const attachmentInputRef = useRef(null);
  const downloadInputRef = useRef(null);

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFileError(null);
    if (!f.type.startsWith("video/")) {
      setFileError("Alleen videobestanden toegestaan");
      return;
    }
    setFile(f);
    const video = document.createElement("video");
    video.preload = "metadata";
    video.src = URL.createObjectURL(f);
    video.onloadedmetadata = () => {
      onChange({ duration_seconds: Math.round(video.duration || 0) });
      URL.revokeObjectURL(video.src);
    };
  };

  const handleAddAttachment = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setAttachmentBusy(true);
    try {
      const result = await uploadMedia(f);
      onChange({
        attachments: [
          ...(lesson.attachments || []),
          {
            kind: result.kind,
            key: result.key,
            mime: result.mime,
            url: result.url,
            name: result.name || f.name,
          },
        ],
      });
    } catch (err) {
      setSaveError("Bijlage upload mislukt");
    } finally {
      setAttachmentBusy(false);
      e.target.value = "";
    }
  };

  const removeAttachment = (index) => {
    onChange({
      attachments: (lesson.attachments || []).filter((_, i) => i !== index),
    });
  };

  const uploadDownloadFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setDownloadFileBusy(true);
    try {
      const result = await uploadMedia(f);
      onChange({ file_key: result.key });
    } catch (err) {
      setSaveError("Bestand upload mislukt");
    } finally {
      setDownloadFileBusy(false);
      e.target.value = "";
    }
  };

  const handleSubmit = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      await onSave(lesson, file, {
        durationSeconds: lesson.duration_seconds,
        onProgress: setProgress,
      });
    } catch (e) {
      setSaveError("Kon les niet opslaan");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={saving ? undefined : onClose}
      title={lesson.id ? "Les bewerken" : "Nieuwe les"}
      isDark={isDark}
      maxWidth="max-w-3xl"
    >
      <div className="space-y-4 max-h-[75vh] overflow-y-auto pr-1">
        <FormField label="Type" isDark={isDark}>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {LESSON_TYPES.map((t) => {
              const Icon = t.icon;
              const active = lesson.type === t.value;
              return (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => onChange({ type: t.value })}
                  className={`flex items-start gap-2 text-left text-[12px] font-medium px-3 py-2 rounded-md border transition-all ${
                    active
                      ? isDark
                        ? "bg-[#1E4FCF]/15 border-[#1E4FCF]/50 text-[#7da6ff]"
                        : "bg-[#1E4FCF]/10 border-[#1E4FCF]/30 text-[#1E4FCF]"
                      : isDark
                        ? "bg-white/[0.03] border-white/[0.06] text-gray-400 hover:bg-white/[0.06]"
                        : "bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  <Icon size={14} className="mt-0.5 flex-shrink-0" />
                  <span className="flex flex-col">
                    <span>{t.label}</span>
                    <span
                      className={`text-[10px] font-normal mt-0.5 ${
                        isDark ? "text-gray-500" : "text-gray-500"
                      }`}
                    >
                      {t.hint}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </FormField>

        <FormField label="Titel" isDark={isDark}>
          <input
            type="text"
            value={lesson.title}
            onChange={(e) => onChange({ title: e.target.value })}
            placeholder="bv. Hoofdstuk 1: Aan de slag"
            className={inputClass(isDark)}
          />
        </FormField>

        <FormField label="Module" isDark={isDark}>
          <select
            value={lesson.module_id || ""}
            onChange={(e) => onChange({ module_id: e.target.value || null })}
            className={inputClass(isDark)}
          >
            <option value="">— Geen module —</option>
            {modules.map((m) => (
              <option key={m.id} value={m.id}>
                {m.title}
              </option>
            ))}
          </select>
        </FormField>

        {lesson.type === "video" && (
          <FormField
            label="Video"
            hint={
              lesson.video_key ? "Vervang door nieuw bestand" : "(MP4 / WebM / MOV)"
            }
            isDark={isDark}
          >
            <div
              className={`rounded-md border-2 border-dashed p-4 text-center cursor-pointer transition-colors ${
                isDark
                  ? "border-white/[0.08] hover:border-white/[0.15]"
                  : "border-gray-200 hover:border-gray-300"
              }`}
              onClick={() => inputRef.current?.click()}
            >
              {file ? (
                <div className={`text-[12px] ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  <Video
                    size={20}
                    className={`mx-auto mb-1.5 ${
                      isDark ? "text-emerald-400" : "text-emerald-500"
                    }`}
                  />
                  <div className="font-medium">{file.name}</div>
                  <div className={`text-[11px] mt-0.5 ${isDark ? "text-gray-500" : "text-gray-500"}`}>
                    {(file.size / 1024 / 1024).toFixed(1)} MB · {formatDuration(lesson.duration_seconds)}
                  </div>
                </div>
              ) : lesson.video_key ? (
                <div className={`text-[12px] ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  <CheckCircle2 size={20} className="mx-auto mb-1.5 text-emerald-400" />
                  Video al geüpload — klik om te vervangen
                </div>
              ) : (
                <div className={`text-[12px] ${isDark ? "text-gray-500" : "text-gray-500"}`}>
                  <Upload
                    size={20}
                    className={`mx-auto mb-1.5 ${
                      isDark ? "text-gray-600" : "text-gray-400"
                    }`}
                  />
                  Klik om een videobestand te selecteren
                </div>
              )}
              <input
                ref={inputRef}
                type="file"
                accept="video/*"
                onChange={handleFile}
                className="hidden"
              />
            </div>
            {fileError && (
              <span className={`text-[11px] mt-1 inline-block ${isDark ? "text-red-400" : "text-red-600"}`}>
                {fileError}
              </span>
            )}
            {saving && progress > 0 && progress < 100 && (
              <div className="mt-2">
                <div className={`text-[11px] mb-1 ${isDark ? "text-gray-500" : "text-gray-500"}`}>
                  Uploaden… {progress}%
                </div>
                <div className={`h-1.5 rounded-full overflow-hidden ${isDark ? "bg-white/[0.06]" : "bg-gray-100"}`}>
                  <div className="h-full bg-[#1E4FCF] transition-all" style={{ width: `${progress}%` }} />
                </div>
              </div>
            )}
          </FormField>
        )}

        {lesson.type === "embed" && (
          <FormField label="Embed URL" hint="YouTube, Vimeo, of andere iframe-vriendelijke URL" isDark={isDark}>
            <input
              type="url"
              value={lesson.embed_url || ""}
              onChange={(e) => onChange({ embed_url: e.target.value })}
              placeholder="https://www.youtube.com/watch?v=..."
              className={inputClass(isDark)}
            />
          </FormField>
        )}

        {lesson.type === "live" && (
          <FormField label="Sessielink" hint="Zoom / Teams / Google Meet" isDark={isDark}>
            <input
              type="url"
              value={lesson.embed_url || ""}
              onChange={(e) => onChange({ embed_url: e.target.value })}
              placeholder="https://zoom.us/j/..."
              className={inputClass(isDark)}
            />
          </FormField>
        )}

        {lesson.type === "file" && (
          <FormField label="Downloadbaar bestand" isDark={isDark}>
            <div
              className={`rounded-md border p-3 flex items-center gap-3 ${
                isDark
                  ? "border-white/[0.08] bg-white/[0.02]"
                  : "border-gray-200 bg-gray-50"
              }`}
            >
              <Paperclip
                size={16}
                className={isDark ? "text-gray-500" : "text-gray-400"}
              />
              <div className="flex-1 min-w-0">
                {lesson.file_key ? (
                  <span className={`text-[12px] truncate block ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Geüpload: {lesson.file_key.split("/").pop()}
                  </span>
                ) : (
                  <span className={`text-[12px] ${isDark ? "text-gray-500" : "text-gray-500"}`}>
                    Nog geen bestand geüpload
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={() => downloadInputRef.current?.click()}
                disabled={downloadFileBusy}
                className={buttonClass(isDark, "secondary")}
              >
                {downloadFileBusy ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
                {lesson.file_key ? "Vervang" : "Upload"}
              </button>
              <input
                ref={downloadInputRef}
                type="file"
                className="hidden"
                onChange={uploadDownloadFile}
              />
            </div>
          </FormField>
        )}

        {(lesson.type === "text" ||
          lesson.type === "video" ||
          lesson.type === "embed" ||
          lesson.type === "live" ||
          lesson.type === "file") && (
          <FormField label="Inhoud" hint="Rich text — afbeeldingen, links, headings" isDark={isDark}>
            <RichTextEditor
              value={lesson.content}
              onChange={(html) => onChange({ content: html })}
              placeholder="Schrijf hier de tekst van de les..."
              isDark={isDark}
              minHeight={lesson.type === "text" ? 280 : 160}
            />
          </FormField>
        )}

        {lesson.type === "quiz" && (
          <FormField label="Quiz" isDark={isDark}>
            <QuizEditor
              value={lesson.quiz_data}
              onChange={(data) => onChange({ quiz_data: data })}
              isDark={isDark}
            />
          </FormField>
        )}

        {/* Extra attachments (for any lesson type) */}
        <FormField
          label="Extra bijlagen"
          hint="Worden bij elke les getoond (foto's, downloads)"
          isDark={isDark}
        >
          <div className="space-y-2">
            {(lesson.attachments || []).length === 0 ? (
              <p
                className={`text-[11px] ${
                  isDark ? "text-gray-500" : "text-gray-500"
                }`}
              >
                Nog geen bijlagen toegevoegd
              </p>
            ) : (
              <div className="space-y-1">
                {(lesson.attachments || []).map((att, i) => (
                  <div
                    key={i}
                    className={`flex items-center gap-2 p-2 rounded border text-[12px] ${
                      isDark
                        ? "border-white/[0.06] bg-white/[0.02]"
                        : "border-gray-200 bg-white"
                    }`}
                  >
                    {att.kind === "image" ? (
                      <ImageIcon size={13} className={isDark ? "text-gray-500" : "text-gray-400"} />
                    ) : att.kind === "video" ? (
                      <Video size={13} className={isDark ? "text-gray-500" : "text-gray-400"} />
                    ) : (
                      <Paperclip size={13} className={isDark ? "text-gray-500" : "text-gray-400"} />
                    )}
                    <span className={`flex-1 truncate ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                      {att.name || att.key?.split("/").pop() || "bijlage"}
                    </span>
                    {att.url && (
                      <a
                        href={att.url}
                        target="_blank"
                        rel="noreferrer"
                        className={`p-1 rounded ${
                          isDark
                            ? "text-gray-500 hover:text-gray-200"
                            : "text-gray-400 hover:text-gray-700"
                        }`}
                      >
                        <Download size={11} />
                      </a>
                    )}
                    <button
                      type="button"
                      onClick={() => removeAttachment(i)}
                      className={`p-1 rounded ${
                        isDark
                          ? "text-gray-500 hover:text-red-400"
                          : "text-gray-400 hover:text-red-500"
                      }`}
                    >
                      <Trash2 size={11} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <button
              type="button"
              onClick={() => attachmentInputRef.current?.click()}
              disabled={attachmentBusy}
              className={buttonClass(isDark, "ghost")}
            >
              {attachmentBusy ? <Loader2 size={11} className="animate-spin" /> : <Paperclip size={11} />}
              Bijlage toevoegen
            </button>
            <input
              ref={attachmentInputRef}
              type="file"
              className="hidden"
              onChange={handleAddAttachment}
            />
          </div>
        </FormField>

        <label
          className={`flex items-center gap-2 text-[13px] cursor-pointer select-none ${
            isDark ? "text-gray-300" : "text-gray-700"
          }`}
        >
          <input
            type="checkbox"
            checked={!!lesson.preview}
            onChange={(e) => onChange({ preview: e.target.checked })}
          />
          <Eye size={13} className="text-amber-400" />
          Gratis preview — niet-ingeschreven studenten kunnen deze les bekijken
        </label>

        {saveError && (
          <div
            className={`text-[12px] px-3 py-2 rounded-md ${
              isDark ? "bg-red-500/10 text-red-400" : "bg-red-50 text-red-600"
            }`}
          >
            {saveError}
          </div>
        )}

        <div
          className={`flex justify-end gap-2 pt-3 border-t sticky bottom-0 ${
            isDark ? "border-white/[0.06] bg-[#0f0f0f]" : "border-gray-100 bg-white"
          }`}
        >
          <button onClick={onClose} disabled={saving} className={buttonClass(isDark, "ghost")}>
            Annuleer
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving || !lesson.title.trim()}
            className={buttonClass(isDark, "primary")}
          >
            {saving && <Loader2 size={13} className="animate-spin" />}
            {file ? "Opslaan & uploaden" : "Opslaan"}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default LessonEditorModal;
